import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Sparkles, BadgeCheck } from "lucide-react";
import { CATEGORIES } from "@/data/services";
import { TID } from "@/constants/testIds/site";

function ServiceCard({ item, accent, onOrder }) {
  const [variantIdx, setVariantIdx] = useState(0);
  const hasVariants = Array.isArray(item.variants) && item.variants.length > 0;
  const active = hasVariants ? item.variants[variantIdx] : null;
  const displayPrice = active?.price || item.price;
  const displayUnit = active?.unit || item.unit;

  const buildLabel = () => {
    if (hasVariants) return `${item.title} — ${active.label}`;
    return item.title;
  };

  return (
    <div
      data-testid={TID.services.card(item.id)}
      className="group border border-white/10 rounded-xl p-5 hover:border-[#d4af37]/50 transition-colors relative bg-black/30 flex flex-col"
    >
      {item.popular && (
        <span
          className="absolute -top-2 right-4 text-[9px] uppercase tracking-[0.3em] px-2 py-0.5 rounded-full font-semibold"
          style={{ background: "#d4af37", color: "#000" }}
        >
          Populaire
        </span>
      )}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="text-white font-medium text-[15px] leading-snug">
            {item.title}
          </div>
          <div className="text-white/50 text-xs mt-2 leading-relaxed">
            {item.desc}
          </div>
        </div>
        <ArrowUpRight
          size={16}
          className="text-white/30 group-hover:text-[#d4af37] transition-colors mt-1"
        />
      </div>

      {hasVariants && (
        <div className="mt-4 flex gap-2">
          {item.variants.map((v, i) => (
            <button
              key={v.label}
              onClick={() => setVariantIdx(i)}
              data-testid={`${TID.services.card(item.id)}-variant-${i}`}
              className={`flex-1 px-3 py-2 rounded-full text-[10px] uppercase tracking-widest transition-colors border ${
                i === variantIdx
                  ? "bg-[#d4af37]/10 border-[#d4af37]/50 text-[#d4af37]"
                  : "border-white/10 text-white/50 hover:text-white/80 hover:border-white/30"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
      )}

      <div className="mt-5 flex items-end justify-between gap-3">
        <div>
          <div className="text-gold-gradient font-display text-2xl leading-none whitespace-nowrap">
            {displayPrice}
          </div>
          <div className="text-white/40 text-[11px] uppercase tracking-widest mt-1">
            {displayUnit}
          </div>
        </div>
        <button
          data-testid={TID.services.orderBtn(item.id)}
          onClick={() => onOrder(buildLabel())}
          className="text-[11px] uppercase tracking-[0.25em] text-white/70 hover:text-[#d4af37] transition-colors"
        >
          Commander →
        </button>
      </div>
    </div>
  );
}

export default function ServicesBento({ onOrder }) {
  const [activeKey, setActiveKey] = useState(CATEGORIES[0].key);
  const active = CATEGORIES.find((c) => c.key === activeKey) || CATEGORIES[0];

  return (
    <section
      id="services"
      data-testid={TID.services.section}
      className="relative py-28 md:py-36 px-6 md:px-10"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6">
          <div>
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.4em] text-white/50 mb-4">
              <span className="dot-gold" />
              Catalogue complet
            </div>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tighter text-white max-w-3xl">
              Une offre <span className="text-gold-gradient italic">complète</span>,
              taillée pour vos ambitions.
            </h2>
          </div>
          <p className="text-white/60 max-w-md text-base leading-relaxed">
            Abonnés, vues, likes, monétisation, certification… tout pour faire
            décoller vos réseaux sociaux à des prix imbattables.
          </p>
        </div>

        {/* Category tabs */}
        <div
          className="flex flex-wrap gap-2 md:gap-3 mb-10 border-b border-white/10 pb-2 overflow-x-auto"
          role="tablist"
          data-testid="services-tabs"
        >
          {CATEGORIES.map((c) => {
            const isActive = c.key === activeKey;
            return (
              <button
                key={c.key}
                role="tab"
                data-testid={`services-tab-${c.key}`}
                onClick={() => setActiveKey(c.key)}
                className={`relative px-5 py-3 rounded-full text-sm uppercase tracking-[0.2em] whitespace-nowrap transition-colors ${
                  isActive
                    ? "text-white"
                    : "text-white/45 hover:text-white/80"
                }`}
                style={
                  isActive
                    ? {
                        background: `${c.accent}15`,
                        border: `1px solid ${c.accent}55`,
                      }
                    : undefined
                }
              >
                <span className="text-[10px] mr-2 opacity-70">
                  0{CATEGORIES.indexOf(c) + 1}
                </span>
                {c.label}
                {isActive && (
                  <span
                    className="absolute left-1/2 -translate-x-1/2 -bottom-[9px] w-1.5 h-1.5 rounded-full"
                    style={{ background: c.accent }}
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Category content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active.key}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4 }}
            className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#111]"
          >
            <div className="p-8 md:p-10 relative">
              <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <span
                    className="text-[10px] uppercase tracking-[0.4em] px-3 py-1 rounded-full border"
                    style={{
                      color: active.accent,
                      borderColor: `${active.accent}55`,
                    }}
                  >
                    {active.tag}
                  </span>
                  {active.key === "certification" && (
                    <span className="flex items-center gap-2 text-white/40 text-xs">
                      <BadgeCheck size={14} className="text-[#3B82F6]" />
                      Compte certifié officiel
                    </span>
                  )}
                </div>
                <Sparkles size={16} className="text-white/30" />
              </div>

              <h3 className="font-display text-4xl md:text-5xl text-white tracking-tighter mb-8 flex items-center gap-3">
                <span className="text-3xl md:text-4xl">{active.emoji}</span>
                {active.label}
              </h3>

              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                {active.items.map((it) => (
                  <ServiceCard
                    key={it.id}
                    item={it}
                    accent={active.accent}
                    onOrder={onOrder}
                  />
                ))}
              </div>
            </div>

            {/* Corner accent */}
            <div
              className="absolute top-0 right-0 w-40 h-40 opacity-20 blur-3xl pointer-events-none"
              style={{ background: active.accent }}
            />
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
